import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dbLabel } from "../../i18n/dbLabels";
import { userBookMarkApi, removeBookMarkApi, getUserBookMarkListApi } from "../../apis/WebServices";
import { UserContext } from "../../contexts/UserContext";
import { message } from "antd";
import Loader from "../loader/Loader";
import { useNavigate } from "react-router-dom";
import { app } from "@microsoft/teams-js";
import { videoDeepLink } from "../../apis/env";
import useVideos from "../../hooks/useVideos";
import VideoCatalogue from "../../services/VideoCatalogue";

// Import training icon for all subcategories (dumbbell icon)
import trainingIcon from "../../assets/training-blue.png";

// Import shared skeleton system
import { SkeletonVideoCard } from "../skeletons";

const VideosCatalogue = () => {
    const { userToken } = useContext(UserContext);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [videos, setVideos] = useState([]);
    const [allVideos, setAllVideos] = useState([]); // Store all videos for filtering
    const [bookMarkedIds, setBookMarkedIds] = useState([]);
    const [bookmarkLoadingIds, setBookmarkLoadingIds] = useState([]);
    
    // Category filtering state
    const [selectedCategories, setSelectedCategories] = useState({
        'Lower Body': true,
        'Upper Body': true, 
        'Core Stability': true
    });
    
    // 🔍 DEBUG: Component mounted
    console.log('🎬 Video Catalogue component mounted!');
    
    // 🔍 DEBUG: Check for navigation parameters from Teams SDK
    useEffect(() => {
        const checkNavigationParameters = async () => {
            try {
                console.log('🔍 VIDEO CATALOGUE: Checking for navigation parameters...');
                
                // Check URL parameters first
                const urlParams = new URLSearchParams(window.location.search);
                const urlVideoId = urlParams.get('videoId');
                const urlVId = urlParams.get('vId');
                
                console.log('🔍 VIDEO CATALOGUE: URL parameters:', { urlVideoId, urlVId });
                
                // Check Teams SDK context
                if (window.microsoftTeams && window.microsoftTeams.app) {
                    await window.microsoftTeams.app.initialize();
                    const context = await window.microsoftTeams.app.getContext();
                    
                    console.log('🔍 VIDEO CATALOGUE: Teams context:', context);
                    console.log('🔍 VIDEO CATALOGUE: subPageId:', context?.page?.subPageId);
                    
                    if (context?.page?.subPageId) {
                        const subPageParams = new URLSearchParams(context.page.subPageId.replace('|', '&'));
                        const teamsVideoId = subPageParams.get('videoId');
                        const teamsVId = subPageParams.get('vId');
                        
                        console.log('🔍 VIDEO CATALOGUE: Teams subPageId parameters:', { teamsVideoId, teamsVId });
                        
                        if (teamsVideoId && teamsVId) {
                            console.log('🎯 VIDEO CATALOGUE: Found video parameters! Navigating to video detail:', {
                                videoId: teamsVideoId,
                                vId: teamsVId
                            });
                            
                            // 🔥 IMPORTANT: Clear the Teams subPageId BEFORE navigating to prevent infinite loop
                            console.log('🧹 VIDEO CATALOGUE: Clearing Teams subPageId to prevent navigation loop');
                            
                            try {
                                // Clear the subPageId by updating Teams context
                                if (window.microsoftTeams && window.microsoftTeams.pages) {
                                    await window.microsoftTeams.pages.config.setConfig({
                                        contentUrl: window.location.origin + '/#/video-catalogue',
                                        websiteUrl: window.location.origin + '/#/video-catalogue'
                                    });
                                }
                            } catch (clearError) {
                                console.warn('⚠️ VIDEO CATALOGUE: Could not clear Teams subPageId:', clearError);
                            }
                            
                            // Navigate to video detail immediately
                            navigate(`/video-detail?videoId=${teamsVideoId}&vId=${teamsVId}&from=home`);
                            return;
                        }
                    }
                }
                
                // If we have URL parameters, use those
                if (urlVideoId && urlVId) {
                    console.log('🎯 VIDEO CATALOGUE: Found URL video parameters! Navigating to video detail:', {
                        videoId: urlVideoId,
                        vId: urlVId
                    });
                    
                    navigate(`/video-detail?videoId=${urlVideoId}&vId=${urlVId}&from=video-catalogue`);
                    return;
                }
                
                console.log('🔍 VIDEO CATALOGUE: No video parameters found, showing catalogue view');
                
            } catch (error) {
                console.error('❌ VIDEO CATALOGUE: Error checking navigation parameters:', error);
            }
        };
        
        checkNavigationParameters();
    }, [navigate]);
    
    // Use clean video service
    const { 
        data: videoData, 
        loading, 
        error, 
        fetchData 
    } = useVideos('list', {
        userId: userToken,
        pageSize: 1000,
        pageNumber: 1
    });

    // Process videos when data changes
    useEffect(() => {
        if (videoData?.videos) {
            const allVideos = videoData.videos;
            
            // Store all videos for filtering
            setAllVideos(allVideos);
            
            // Apply initial filtering
            filterVideosByCategory(allVideos, selectedCategories);
            
            // FIXED: Fetch bookmarks from dedicated API instead of relying on video.isBookMarked
            fetchBookmarkedVideoIds();
        }
    }, [videoData, selectedCategories]);
    
    // FIXED: Dedicated function to fetch bookmarked video IDs
    const fetchBookmarkedVideoIds = async () => {
        try {
            if (!userToken || userToken === 'undefined') {
                console.log('⚠️ Video Catalogue: No user token, skipping bookmark fetch');
                return;
            }
            
            const response = await getUserBookMarkListApi(1, 1000, userToken);
            console.log('🔍 Video Catalogue: Raw bookmark response:', response);
            
            if (response?.isSuccess) {
                // CRITICAL FIX: Handle multiple possible response structures
                let bookmarkData = response.data;
                
                // Check if response.data is directly an array
                if (Array.isArray(bookmarkData)) {
                    console.log('✅ Video Catalogue: response.data is array:', bookmarkData.length, 'items');
                } else if (bookmarkData && typeof bookmarkData === 'object') {
                    // Try different nested properties
                    if (Array.isArray(bookmarkData.videos)) {
                        bookmarkData = bookmarkData.videos;
                        console.log('✅ Video Catalogue: Using response.data.videos:', bookmarkData.length, 'items');
                    } else if (Array.isArray(bookmarkData.items)) {
                        bookmarkData = bookmarkData.items;
                        console.log('✅ Video Catalogue: Using response.data.items:', bookmarkData.length, 'items');
                    } else if (Array.isArray(bookmarkData.bookmarks)) {
                        bookmarkData = bookmarkData.bookmarks;
                        console.log('✅ Video Catalogue: Using response.data.bookmarks:', bookmarkData.length, 'items');
                    } else {
                        console.warn('⚠️ Video Catalogue: response.data structure not recognized:', Object.keys(bookmarkData));
                        bookmarkData = [];
                    }
                } else {
                    console.warn('⚠️ Video Catalogue: response.data is not an object or array:', typeof bookmarkData, bookmarkData);
                    bookmarkData = [];
                }
                
                if (Array.isArray(bookmarkData)) {
                    const bookmarkedIds = bookmarkData.map(item => item.videoId || item.id).filter(id => id);
                    setBookMarkedIds(bookmarkedIds);
                    console.log('✅ Video Catalogue: Bookmarked IDs fetched from API:', bookmarkedIds);
                    console.log('📋 Video Catalogue: State persistence - Loaded', bookmarkedIds.length, 'bookmarked videos');
                } else {
                    console.error('❌ Video Catalogue: Final bookmarkData is not an array:', typeof bookmarkData, bookmarkData);
                    setBookMarkedIds([]);
                }
            } else {
                console.log('📋 Video Catalogue: No bookmarked videos found or API call failed');
                setBookMarkedIds([]);
            }
        } catch (error) {
            console.error('❌ Video Catalogue: Failed to fetch bookmarked IDs:', error);
            setBookMarkedIds([]);
        }
    };
    
    // Function to filter videos by selected subcategories
    const filterVideosByCategory = (videoList, categories) => {
        console.log('🎯 Video Catalogue: Filtering videos:', { videoList: videoList.length, categories });
        
        const selectedCategoryNames = Object.keys(categories).filter(cat => categories[cat]);
        console.log('📝 Video Catalogue: Selected categories:', selectedCategoryNames);
        
        if (selectedCategoryNames.length === 0) {
            // If no categories selected, show no videos
            console.log('❌ Video Catalogue: No categories selected, showing no videos');
            setVideos([]);
            return;
        }
        
        const filteredVideos = videoList.filter(video => {
            const videoSubCategory = video?.subCategory?.name || '';
            const isMatch = selectedCategoryNames.includes(videoSubCategory);
            return isMatch;
        });
        
        console.log('✅ Video Catalogue: Filtered videos result:', filteredVideos.length);
        setVideos(filteredVideos);
    };
    
    // Handle category toggle
    const handleCategoryToggle = (category) => {
        const newSelectedCategories = {
            ...selectedCategories,
            [category]: !selectedCategories[category]
        };
        
        setSelectedCategories(newSelectedCategories);
        filterVideosByCategory(allVideos, newSelectedCategories);
    };

    const handleBookmark = async (videoId) => {
        try {
            if (!userToken) {
                message.error(t('videoCatalogue.userNotLoggedIn'));
                return;
            }

            console.log('🔖 Video Catalogue: Bookmark operation started', { videoId, isCurrentlyBookmarked: bookMarkedIds.includes(videoId) });
            
            // CRITICAL FIX: Prevent multiple clicks during operation
            if (bookmarkLoadingIds.includes(videoId)) {
                console.log('⚠️ Video Catalogue: Bookmark operation already in progress for video:', videoId);
                return;
            }
            
            setBookmarkLoadingIds(prev => [...prev, videoId]);
            
            const isCurrentlyBookmarked = bookMarkedIds.includes(videoId);
            
            // CRITICAL FIX: Update UI immediately for instant feedback
            if (isCurrentlyBookmarked) {
                setBookMarkedIds(prev => prev.filter(id => id !== videoId));
            } else {
                setBookMarkedIds(prev => [...prev, videoId]);
            }

            if (isCurrentlyBookmarked) {
                console.log('🗑️ Video Catalogue: Removing bookmark for video:', videoId);
                const response = await removeBookMarkApi(videoId, userToken);
                if (response.isSuccess) {
                    message.success(response?.message || t('videoCatalogue.bookmarkRemoved'));
                    console.log('✅ Video Catalogue: Bookmark removed successfully');
                    // Refresh from API after delay to ensure consistency
                    setTimeout(() => fetchBookmarkedVideoIds(), 500);
                } else {
                    // REVERT UI change if API failed
                    setBookMarkedIds(prev => [...prev, videoId]);
                    message.error(response?.message || t('videoCatalogue.bookmarkRemoveFailed'));
                }
            } else {
                console.log('➕ Video Catalogue: Adding bookmark for video:', videoId);
                const response = await userBookMarkApi(videoId, userToken);
                if (response.isSuccess) {
                    message.success(response?.message || t('videoCatalogue.bookmarkAdded'));
                    console.log('✅ Video Catalogue: Bookmark added successfully');
                    // Refresh from API after delay to ensure consistency
                    setTimeout(() => fetchBookmarkedVideoIds(), 500);
                } else {
                    // REVERT UI change if API failed
                    setBookMarkedIds(prev => prev.filter(id => id !== videoId));
                    message.error(response?.message || t('videoCatalogue.bookmarkAddFailed'));
                }
            }
        } catch (error) {
            console.error('❌ Video Catalogue: Bookmark operation failed:', error);
            // REVERT UI change if error occurred
            const isCurrentlyBookmarked = bookMarkedIds.includes(videoId);
            if (isCurrentlyBookmarked) {
                setBookMarkedIds(prev => [...prev, videoId]);
            } else {
                setBookMarkedIds(prev => prev.filter(id => id !== videoId));
            }
            message.error(error?.message || t('videoCatalogue.bookmarkAddFailed'));
        } finally {
            setBookmarkLoadingIds(prev => prev.filter(id => id !== videoId));
        }
    };

    const handleVideoClick = (video) => {
        const videoId = video?.id;
        const categoryId = video?.categoryId || video?.category?.id;

        if (!videoId || !categoryId) {
            console.error('Missing video or category ID:', { videoId, categoryId });
            message.error(t('videoCatalogue.openVideoFailed'));
            return;
        }

        console.log('🎯 VideosCatalogue: Navigating to video detail', { videoId, categoryId });
        
        // Use React Router navigation within the app
        navigate(`/video-detail?videoId=${videoId}&vId=${categoryId}`);
    };

    // Fetch videos when userToken is available
    useEffect(() => {
        if (userToken) {
            console.log('🚀 Video Catalogue: Fetching videos for user:', userToken);
            fetchData();
        }
    }, [userToken]); // 🔧 CRITICAL FIX: Remove fetchData dependency to prevent infinite loops

    // Handle errors
    useEffect(() => {
        if (error) {
            console.error('❌ Video Catalogue error:', error);
            message.error(t('videoCatalogue.loadVideosFailed') + ': ' + error);
        }
    }, [error]);
    
    // Add debug logging to see what's happening with filtering
    useEffect(() => {
        console.log('🔍 Video Catalogue filtering debug:', {
            selectedCategories,
            allVideosCount: allVideos.length,
            filteredVideosCount: videos.length,
            loading,
            error
        });
    }, [selectedCategories, allVideos, videos, loading, error]);

    if (loading) {
        return (
            <div style={{ minHeight: "100vh" }}>
                <div className="container-fluid">
                    <div className="row">
                        <div className="whole-div">
                            <div className="row page-content">
                                <div className="col-12">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h2 className="text-white mb-0" style={{ fontWeight: 'bold' }}>{t('videoCatalogue.title')}</h2>
                                        <div className="d-flex align-items-center gap-3">
                                            <a style={{ color: '#6c757d', cursor: 'default' }}>
                                                <i className="ri ri-arrow-left-s-line" style={{ fontWeight: 'bold' }}></i> {t('common.back')}
                                            </a>
                                            <div style={{
                                                width: '1px',
                                                height: '30px',
                                                backgroundColor: '#6c757d',
                                                margin: '0 8px'
                                            }}></div>
                                            <div className="d-flex gap-3">
                                                {[
                                                    { key: 'Lower Body', label: t('videoCatalogue.categoryLowerBody') },
                                                    { key: 'Upper Body', label: t('videoCatalogue.categoryUpperBody') },
                                                    { key: 'Core Stability', label: t('videoCatalogue.categoryCoreStability') },
                                                ].map(({ key, label }) => (
                                                    <button
                                                        key={key}
                                                        disabled
                                                        className="btn btn-outline-secondary"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            borderColor: '#4a4e59',
                                                            color: '#6c757d',
                                                            fontWeight: '500',
                                                            fontSize: '14px',
                                                            padding: '8px 16px',
                                                            borderRadius: '20px',
                                                            minWidth: '130px',
                                                            cursor: 'not-allowed'
                                                        }}
                                                    >
                                                        <img
                                                            src={trainingIcon}
                                                            alt={label}
                                                            style={{
                                                                width: '20px',
                                                                height: '20px',
                                                                marginRight: '8px',
                                                                filter: 'brightness(0) saturate(100%) invert(35%) sepia(11%) saturate(801%) hue-rotate(184deg) brightness(96%) contrast(91%)',
                                                                opacity: 0.5
                                                            }}
                                                        />
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                {/* Show 8 skeleton cards using shared component */}
                                {[...Array(8)].map((_, index) => (
                                    <SkeletonVideoCard key={index} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh" }}>
            <div className="container-fluid">
                <div className="row">
                    <div className="whole-div">
                        <div className="row page-content">
                            <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h2 className="text-white mb-0" style={{ fontWeight: 'bold' }}>{t('videoCatalogue.title')}</h2>
                                    
                                    {/* Back Button + Category Filter Buttons */}
                                    <div className="d-flex align-items-center gap-3">
                                        {/* Back Button */}
                                        <a 
                                            onClick={() => {
                                                console.log('🔙 BACK BUTTON: Video Catalogue -> Home tab');
                                                
                                                try {
                                                    if (window.microsoftTeams && window.microsoftTeams.pages && window.microsoftTeams.pages.isSupported()) {
                                                        const teamsAppId = import.meta.env.VITE_TEAMS_APP_ID;
                                                        window.microsoftTeams.pages.navigateToApp({
                                                            appId: teamsAppId,
                                                            pageId: 'index' // Home tab entity ID
                                                        }).then(() => {
                                                            console.log('✅ Successfully navigated back to Home tab');
                                                        }).catch((error) => {
                                                            console.error('❌ Home tab navigation failed:', error);
                                                            navigate('/home');
                                                        });
                                                    } else {
                                                        console.log('❌ Teams SDK not available, using React Router fallback');
                                                        navigate('/home');
                                                    }
                                                } catch (error) {
                                                    console.error('❌ Navigation error:', error);
                                                    navigate('/home');
                                                }
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                transition: 'color 0.2s ease',
                                                color: '#fff'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.color = '#16f69f';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.color = '#fff';
                                            }}
                                        >
                                            <i className="ri ri-arrow-left-s-line" style={{ fontWeight: 'bold' }}></i> {t('common.back')}
                                        </a>
                                        
                                        {/* Vertical Separator */}
                                        <div style={{
                                            width: '1px',
                                            height: '30px',
                                            backgroundColor: '#6c757d',
                                            margin: '0 8px'
                                        }}></div>
                                        
                                        {/* Category Filter Buttons */}
                                        <div className="d-flex gap-3">
                                            {[
                                                { key: 'Lower Body', icon: trainingIcon, label: t('videoCatalogue.categoryLowerBody') },
                                                { key: 'Upper Body', icon: trainingIcon, label: t('videoCatalogue.categoryUpperBody') },
                                                { key: 'Core Stability', icon: trainingIcon, label: t('videoCatalogue.categoryCoreStability') }
                                            ].map(({ key, icon, label }) => (
                                                <button
                                                    key={key}
                                                    onClick={() => handleCategoryToggle(key)}
                                                    className={`btn d-flex align-items-center ${
                                                        selectedCategories[key]
                                                            ? 'btn-outline-light' 
                                                            : 'btn-outline-secondary'
                                                    }`}
                                                    style={{
                                                        backgroundColor: selectedCategories[key] ? '#16f69f' : 'transparent',
                                                        borderColor: selectedCategories[key] ? '#16f69f' : '#6c757d',
                                                        color: selectedCategories[key] ? '#000' : '#fff',
                                                        fontWeight: '500',
                                                        fontSize: '14px',
                                                        padding: '8px 16px',
                                                        borderRadius: '20px',
                                                        transition: 'all 0.2s ease',
                                                        minWidth: '130px'
                                                    }}
                                                >
                                                    <img 
                                                        src={icon} 
                                                        alt={label}
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            marginRight: '8px',
                                                            filter: selectedCategories[key] ? 'none' : 'brightness(0) saturate(100%) invert(47%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)'
                                                        }}
                                                    />
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading || (allVideos.length === 0 && !error) ? (
                            // Loading or initial empty state (before API response)
                            <div className="row">
                                {/* Show skeleton cards during loading and initial state */}
                                {[...Array(8)].map((_, index) => (
                                    <SkeletonVideoCard key={index} />
                                ))}
                            </div>
                        ) : allVideos.length === 0 && error ? (
                            // Only show "no videos" if there's an error OR data was fetched but empty
                            <div className="row">
                                <div className="col-12 d-flex justify-content-center">
                                    <div className="text-center" style={{ maxWidth: '400px' }}>
                                        <h5 className="text-white-50 mb-3">{t('common.noVideos')}</h5>
                                        <p className="text-muted small">{t('videoCatalogue.checkConnection')}</p>
                                    </div>
                                </div>
                            </div>
                        ) : videos.length === 0 ? (
            <div className="row">
                <div className="col-12 d-flex justify-content-center">
                    <div className="text-center" style={{ maxWidth: '400px' }}>
                        <h5 className="text-white-50 mb-3">{t('videoCatalogue.selectCategory')}</h5>
                        <p className="text-muted small">{t('videoCatalogue.selectCategoryHint')}</p>
                    </div>
                </div>
            </div>
        ) : (
                            <div className="row">
                                {videos.map((video) => (
                                    <div key={video.id} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                        <div className="card explore-box card-animate">
                                            <div className="explore-place-bid-img" onClick={() => handleVideoClick(video)}>
                                                <img
                                                    src={video?.thumbnail}
                                                    alt={video?.title}
                                                    className="img-fluid card-img-top explore-img"
                                                    style={{ height: "200px", objectFit: "cover" }}
                                                />
                                                <div className="bg-overlay"></div>
                                                <div className="place-bid-btn">
                                                    <a className="cursor-pointer">
                                                        <i className="ri-play-circle-fill align-bottom me-1"></i>
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h6 className="card-title mb-0 flex-grow-1" 
                                                        style={{ 
                                                            cursor: "pointer", 
                                                            fontSize: "14px",
                                                            lineHeight: "1.2",
                                                            height: "17px",
                                                            overflow: "hidden",
                                                            whiteSpace: "nowrap",
                                                            textOverflow: "ellipsis"
                                                        }} 
                                                        onClick={() => handleVideoClick(video)}>
                                                        <a className="text-primary">
                                                            {video?.title}
                                                        </a>
                                                    </h6>
                                                    <button
                                                        className="btn btn-sm p-0"
                                                        onClick={() => handleBookmark(video.id)}
                                                        disabled={bookmarkLoadingIds.includes(video.id)}
                                                        style={{ background: "none", border: "none" }}
                                                    >
                                                        <i className={`mdi ${bookMarkedIds.includes(video.id) ? "mdi-bookmark" : "mdi-bookmark-outline"} fs-18`} 
                                                           style={{ 
                                                               color: bookMarkedIds.includes(video.id) ? '#16f69f' : '#6c757d',
                                                               fontSize: '18px'
                                                           }}></i>
                                                    </button>
                                                </div>
                                                {video?.subCategory?.name && (
                                                    <p className="text-muted mb-1">
                                                        <small>{dbLabel(video.subCategory.name, i18n.language)}</small>
                                                    </p>
                                                )}
                                                {video?.duration && (
                                                    <p className="text-muted mb-0">
                                                        <small><i className="ri-time-line me-1"></i>{video.duration}</small>
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideosCatalogue;